import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const WORKSPACE_KEY = "glotech_active_workspace";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [activeWorkspace, setActiveWorkspaceState] = useState(
    localStorage.getItem(WORKSPACE_KEY) || null
  );

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const resolveDefaultWorkspace = useCallback((userData) => {
    const workspaces = userData?.workspaces || [];
    if (workspaces.length === 0) return null;

    // Check if stored workspace is still valid
    const stored = localStorage.getItem(WORKSPACE_KEY);
    if (stored && workspaces.some((w) => w.key === stored)) {
      return stored;
    }

    // Auto-select: if only one workspace, use it
    if (workspaces.length === 1) return workspaces[0].key;

    // For admin with multiple workspaces, default to "web"
    if (userData?.role === "admin") return "web";

    // Default to first available
    return workspaces[0].key;
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);

      // Set default workspace if not already set or invalid
      const ws = resolveDefaultWorkspace(userData);
      if (ws) {
        setActiveWorkspaceState(ws);
        localStorage.setItem(WORKSPACE_KEY, ws);
      }
    } catch (error) {
      console.error("Auth error:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;

    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);

    // Set workspace on login
    const ws = resolveDefaultWorkspace(userData);
    if (ws) {
      setActiveWorkspaceState(ws);
      localStorage.setItem(WORKSPACE_KEY, ws);
    }

    return userData;
  };

  const setActiveWorkspace = (key) => {
    setActiveWorkspaceState(key);
    localStorage.setItem(WORKSPACE_KEY, key);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem(WORKSPACE_KEY);
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setActiveWorkspaceState(null);
  };

  const workspaces = user?.workspaces || [];

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isOrgAdmin: user?.role === "org_admin",
    clientId: user?.client_id,
    agencyId: user?.agency_id,
    orgId: user?.org_id,
    // P0: Workspace management
    workspaces,
    activeWorkspace,
    setActiveWorkspace,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
